/**
 * @voltxampere/web — Toast notification store (Sprint 25 R2 lane 1)
 *
 * Signal-based toast queue. Closes the Sprint 13+ commitment that
 * was shipped as a placeholder `<vxa-toast-stack>` for ~12 sprints.
 * Documented gap in TS-007 (Sprint 25 R1 triage).
 *
 * Public API:
 *   - `showToast({ kind, message, title?, duration? })` returns the
 *     freshly minted toast id.
 *   - `dismissToast(id)` removes a toast and clears its timer.
 *   - `dismissAllToasts()` clears the whole queue.
 *   - `toasts` is the read-only signal `<vxa-toast-stack>` subscribes
 *     to; consumers MUST NOT mutate it directly.
 *
 * Queue policy:
 *   - Stack capped at `MAX_TOASTS` (4). Adding a 5th drops the
 *     oldest (FIFO eviction); the dropped toast's timer is cleared.
 *   - Default duration 4 s for every kind. Pass `Infinity` to keep
 *     a toast visible until manually dismissed (recommended for
 *     error escalations the user must acknowledge).
 *
 * Sprint 25 R2 lane 2 (R2-VALIDATION-GATE) wires
 * `runDc/Transient/Ac` runners through `showToast` for engine
 * error propagation.
 *
 * Copyright © 2026 360 Studio
 * Proprietary — All rights reserved.
 */

import { signal, type ReadonlySignal, type Signal } from "@preact/signals-core";

export type ToastKind = "error" | "warning" | "info" | "success";

export interface Toast {
  readonly id: string;
  readonly kind: ToastKind;
  readonly message: string;
  readonly title?: string;
  /** Auto-dismiss after `duration` ms. `Infinity` = manual dismiss only. */
  readonly duration: number;
  /** Wall-clock timestamp the toast was queued (Date.now()). */
  readonly createdAt: number;
}

export const MAX_TOASTS = 4;
export const DEFAULT_TOAST_DURATION_MS = 4_000;

const toastsSignal: Signal<readonly Toast[]> = signal<readonly Toast[]>([]);

/** Read-only view of the active toast queue, ordered oldest → newest. */
export const toasts: ReadonlySignal<readonly Toast[]> = toastsSignal;

const dismissTimers = new Map<string, ReturnType<typeof setTimeout>>();
let nextToastId = 1;

export interface ShowToastOpts {
  readonly kind: ToastKind;
  readonly message: string;
  readonly title?: string;
  /**
   * Override the default 4 s auto-dismiss window. Pass `Infinity`
   * (or any non-finite number) to require a manual dismiss.
   */
  readonly duration?: number;
}

/**
 * Push a new toast onto the queue. Returns the toast id so callers
 * can dismiss it programmatically before the timer fires (e.g. when
 * a user action resolves the underlying error).
 */
export function showToast(opts: ShowToastOpts): string {
  const id = `toast-${String(nextToastId++)}`;
  const duration = opts.duration ?? DEFAULT_TOAST_DURATION_MS;
  const toast: Toast = {
    id,
    kind: opts.kind,
    message: opts.message,
    duration,
    createdAt: Date.now(),
    ...(opts.title !== undefined ? { title: opts.title } : {}),
  };

  const current = toastsSignal.value;
  let next: readonly Toast[];
  if (current.length >= MAX_TOASTS) {
    // FIFO drop the oldest; clear its pending timer if any.
    const dropped = current[0];
    if (dropped !== undefined) {
      const droppedTimer = dismissTimers.get(dropped.id);
      if (droppedTimer !== undefined) {
        clearTimeout(droppedTimer);
        dismissTimers.delete(dropped.id);
      }
    }
    next = [...current.slice(1), toast];
  } else {
    next = [...current, toast];
  }
  toastsSignal.value = next;

  if (Number.isFinite(duration) && duration > 0) {
    const timer = setTimeout(() => {
      dismissToast(id);
    }, duration);
    dismissTimers.set(id, timer);
  }

  return id;
}

/**
 * Remove a toast by id and cancel its pending auto-dismiss timer.
 * No-op when the id does not match an active toast.
 */
export function dismissToast(id: string): void {
  const timer = dismissTimers.get(id);
  if (timer !== undefined) {
    clearTimeout(timer);
    dismissTimers.delete(id);
  }
  const current = toastsSignal.value;
  const next = current.filter((t) => t.id !== id);
  if (next.length !== current.length) {
    toastsSignal.value = next;
  }
}

/**
 * Clear every active toast and cancel every pending timer. Used by
 * `vxa-app` teardown and by tests' `afterEach`.
 */
export function dismissAllToasts(): void {
  for (const timer of dismissTimers.values()) {
    clearTimeout(timer);
  }
  dismissTimers.clear();
  if (toastsSignal.value.length > 0) {
    toastsSignal.value = [];
  }
}

/**
 * Test-only helper. Restores the module to a pristine state — empty
 * queue, cleared timers, id counter reset to 1. Production callers
 * must not invoke this; tests reset via this hook in `afterEach`.
 */
export function __resetToastsForTests(): void {
  dismissAllToasts();
  nextToastId = 1;
}
